// src/pages/BundeslandPage.jsx
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  BadgeCheck,
  ShieldCheck,
  FileText,
  Layers,
  Droplets,
  Drill,
  ThermometerSun,
  Info,
  ChevronDown,
  ExternalLink,
} from "lucide-react";

const ICONS = {
  MapPin,
  BadgeCheck,
  ShieldCheck,
  FileText,
  Layers,
  Droplets,
  Drill,
  ThermometerSun,
  Info,
};

function ensureMeta(name, content) {
  let meta = document.querySelector(`meta[name='${name}']`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function ensureCanonical(href) {
  let canonical = document.querySelector("link[rel='canonical']");
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = href;
}

function FaqItem({ item }) {
  return (
    <details className="group overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <summary className="flex cursor-pointer items-center justify-between gap-4 px-6 py-5 text-left" style={{ listStyle: "none" }}>
        <span className="font-semibold text-gray-900">{item.q}</span>
        <ChevronDown className="h-5 w-5 shrink-0 text-gray-500 transition group-open:rotate-180" />
      </summary>
      <div className="border-t border-gray-100 px-6 py-5">
        <p className="leading-relaxed text-gray-700">{item.a}</p>
        {item.links?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {item.links.map((link) => (
              <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:border-gray-300">
                <ExternalLink className="h-4 w-4" />
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

export default function BundeslandPage({ data }) {
  const safe = useMemo(() => {
    // Minimaler Fallback, damit die Seite nicht crasht, falls Felder fehlen
    return {
      name: data?.name ?? "Bundesland",
      hero: data?.hero ?? {},
      seo: data?.seo ?? {},
      facts: Array.isArray(data?.facts) ? data.facts : [],
      dataCards: Array.isArray(data?.dataCards) ? data.dataCards : [],
      faq: Array.isArray(data?.faq) ? data.faq : [],
      cta: data?.cta ?? {},
    };
  }, [data]);

  // SEO
  useEffect(() => {
    if (safe.seo?.title) document.title = safe.seo.title;
    if (safe.seo?.description) ensureMeta("description", safe.seo.description);
    if (safe.seo?.canonical) ensureCanonical(safe.seo.canonical);
  }, [safe.seo?.title, safe.seo?.description, safe.seo?.canonical]);

  useEffect(() => {
    if (!safe.faq.length) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.stateFaqSchema = safe.name;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: safe.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [safe.faq, safe.name]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#f6f3f2] py-20">
      {/* HERO */}
      <div className="max-w-5xl mx-auto px-6 mb-14">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700">
	  <MapPin className="w-4 h-4 text-red-600" />

	  <Link
	    to="/bundeslaender"
	    className="text-sm font-medium hover:text-red-700 transition"
	  >
	    {safe.hero.eyebrow ?? "Bundesländer"}
	  </Link>

	  <span className="text-gray-500">/ {safe.name}</span>
	</div>

        <h1 className="text-4xl font-bold text-gray-900 tracking-tight mt-6 mb-4">
          {safe.hero.title ?? `Erdwärme in ${safe.name}`}
        </h1>

        {safe.hero.subtitle && (
          <p className="text-lg text-gray-700 max-w-3xl leading-relaxed">{safe.hero.subtitle}</p>
        )}

        {safe.hero.infoBox && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-5 flex gap-3">
            <Info className="w-5 h-5 text-blue-700 mt-0.5" />
            <p className="text-gray-800 leading-relaxed">
              <b>Hinweis:</b> {safe.hero.infoBox}
            </p>
          </div>
        )}
      </div>

      {/* FACTS GRID */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {safe.facts.map((c, idx) => {
            const Icon = ICONS[c.icon] ?? Info;
            return (
              <div key={idx} className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                <div className={`h-1.5 bg-gradient-to-r ${c.color ?? "from-slate-600 to-slate-500"}`} />
                <div className="p-7">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-2xl bg-gradient-to-br ${c.color ?? "from-slate-600 to-slate-500"} shadow-sm`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>

                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900">{c.title}</h2>

                      {Array.isArray(c.bullets) && (
                        <ul className="mt-3 space-y-2 text-gray-700">
                          {c.bullets.map((b, i) => (
                            <li key={i} className="flex gap-2">
                              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {c.note && <p className="text-sm text-gray-500 mt-4 leading-relaxed">{c.note}</p>}

                      {c.link?.href && (
                        <div className="mt-5">
                          <a
                            href={c.link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium text-red-700 hover:text-red-800"
                          >
                            {c.link.label} <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* DATA SECTION */}
        {safe.dataCards.length > 0 && (
          <div className="mt-14 bg-white shadow-sm rounded-2xl p-10 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Daten & Standort-Check</h2>
            <p className="text-gray-700 leading-relaxed max-w-4xl">
              Typische Faktoren: Genehmigung/Schutzgebiete, Platz/Bohrlogistik, Bebauung sowie standortspezifische Untergrundparameter.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              {safe.dataCards.map((c, i) => {
                const Icon = ICONS[c.icon] ?? Info;
                return (
                  <div key={i} className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-white border border-gray-200">
                        <Icon className="w-5 h-5 text-gray-700" />
                      </div>
                      <h3 className="font-semibold text-gray-900">{c.title}</h3>
                    </div>
                    <p className="text-gray-700 mt-3 leading-relaxed">{c.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* FAQ */}
        {safe.faq.length > 0 && (
          <div className="mt-14 bg-white shadow-sm rounded-2xl p-10 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">FAQ – {safe.name}</h2>
            <div className="space-y-4">
              {safe.faq.map((item) => (
                <FaqItem key={item.q} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        {safe.cta?.title && (
          <div className="mt-14 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-10 text-white shadow-lg">
            <h2 className="text-2xl font-bold mb-3">{safe.cta.title}</h2>
            {safe.cta.text && <p className="text-white/90 leading-relaxed max-w-3xl">{safe.cta.text}</p>}

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {safe.cta.primary?.href && (
                <a
                  href={safe.cta.primary.href}
                  title={safe.cta.primary.title ?? safe.cta.primary.label}
                  className="inline-flex items-center justify-center bg-white text-red-700 hover:bg-white/95 px-8 py-4 rounded-full font-semibold shadow-sm transition"
                >
                  {safe.cta.primary.label ?? "Jetzt starten"}
                </a>
              )}

              {safe.cta.secondary?.href && (
                <a
                  href={safe.cta.secondary.href}
                  className="inline-flex items-center justify-center bg-white/10 hover:bg-white/15 px-8 py-4 rounded-full font-semibold border border-white/25 transition"
                >
                  {safe.cta.secondary.label ?? "Mehr erfahren"}
                </a>
              )}
            </div>

            {safe.cta.footnote && <p className="text-sm text-white/70 mt-4">{safe.cta.footnote}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

